import { Capacitor, registerPlugin } from "@capacitor/core";

interface NativeDocumentScannerPlugin {
  scan(): Promise<{ text: string }>;
  recognize(options: { image: string }): Promise<{ text: string }>;
}

interface NativeShareInboxPlugin {
  takePendingShare(): Promise<{ url?: string }>;
}

const NativeDocumentScanner = registerPlugin<NativeDocumentScannerPlugin>(
  "NativeDocumentScanner",
);
const NativeShareInbox = registerPlugin<NativeShareInboxPlugin>("NativeShareInbox");

export const isNative = (): boolean => Capacitor.isNativePlatform();

export const takePendingSharedArticle = async (): Promise<string | null> => {
  const { url } = await NativeShareInbox.takePendingShare();
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export const scanNativeDocument = async (): Promise<string> => {
  const { text } = await NativeDocumentScanner.scan();
  return text;
};

export const recognizeNativeImage = async (image: Blob): Promise<string> => {
  const { text } = await NativeDocumentScanner.recognize({
    image: await blobToDataUrl(image),
  });
  return text;
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const dataUrl = await blobToDataUrl(blob);
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
};

export const shareNativeFile = async (blob: Blob, filename: string): Promise<void> => {
  const [{ Directory, Filesystem }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  await Filesystem.writeFile({
    path: filename,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
  });
  const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
  await Share.share({ title: filename, url: uri, dialogTitle: "Share your erasure" });
};

export const shareNativeText = async (text: string, title: string): Promise<void> => {
  const { Share } = await import("@capacitor/share");
  await Share.share({ title, text, dialogTitle: "Share your erasure" });
};

export const copyNativeText = async (text: string): Promise<void> => {
  const { Clipboard } = await import("@capacitor/clipboard");
  await Clipboard.write({ string: text });
};

type HapticsPlugin = (typeof import("@capacitor/haptics"))["Haptics"];

// Loaded once and kept: a stroke ticks many times a second, and each tick
// awaiting a fresh dynamic import put a promise chain ahead of every word.
let haptics: Promise<HapticsPlugin> | undefined;
let selecting = false;

const loadHaptics = (): Promise<HapticsPlugin> =>
  (haptics ??= import("@capacitor/haptics").then(({ Haptics }) => Haptics));

/**
 * A tick under the finger as a stroke erases.
 *
 * iOS plays a selection tick only from a generator opened by `selectionStart`;
 * `selectionChanged` on its own is silently dropped. Calling it alone, once per
 * word, is what this used to do: a bridge round trip for every word erased, on
 * the thread that delivers touches to the page, for nothing the reader could
 * feel. So the first tick of a stroke opens the generator, `endHaptics` closes
 * it, and callers tick once per pointer event rather than once per word. The
 * calls are not awaited; the bridge delivers them in the order they are sent.
 */
export const tickHaptic = async (): Promise<void> => {
  if (!isNative()) return;
  const opening = !selecting;
  selecting = true;
  const plugin = await loadHaptics();
  if (opening) void plugin.selectionStart();
  void plugin.selectionChanged();
};

/** Closes the generator a stroke's ticks opened. */
export const endHaptics = async (): Promise<void> => {
  if (!isNative() || !selecting) return;
  selecting = false;
  const plugin = await loadHaptics();
  void plugin.selectionEnd();
};
